/**
 * ForgingModal.jsx - Equipment forging (scrolling) UI
 * MapleStory-style scroll system with colored gemstones
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import {
  FORGING_CONFIG,
  GEMSTONES,
  SCROLL_TYPES,
  SCROLL_STAT_TYPES,
  SCROLL_TIERS,
  formatScrollStat,
  getStatLabel,
  getScrollByStatAndTier,
} from '../../data/forging';
import { getForgingInfo, getRemainingSlots, canApplyScroll } from '../../modules/forging';
import ItemChip from '../UI/ItemChip';
import './ForgingModal.css';

function ForgingModal({ item, onClose }) {
  const { playerCharacter, forgeItem, triggerTutorial } = useGame();

  // Trigger tutorial on first open
  useEffect(() => {
    triggerTutorial('forging');
  }, [triggerTutorial]);

  // Selected scroll configuration
  const [selectedStat, setSelectedStat] = useState('attack');
  const [selectedTier, setSelectedTier] = useState(1);

  // Animation states
  const [isForging, setIsForging] = useState(false);
  const [forgeResult, setForgeResult] = useState(null);
  const [animationPhase, setAnimationPhase] = useState(null); // 'forging', 'flash', 'result'

  // Current item state (tracks scroll changes)
  const [currentItem, setCurrentItem] = useState(item);

  // Count gemstones from inventory (gemstones are now inventory items)
  const gemstones = useMemo(() => {
    const counts = { ruby: 0, sapphire: 0, emerald: 0, topaz: 0, amethyst: 0 };
    if (playerCharacter?.inventory) {
      playerCharacter.inventory.forEach(item => {
        if (item?.isGemstone && counts.hasOwnProperty(item.id)) {
          counts[item.id] += item.quantity || 1;
        }
      });
    }
    return counts;
  }, [playerCharacter?.inventory]);

  // Get scroll info for current selection
  const selectedScroll = useMemo(() =>
    getScrollByStatAndTier(selectedStat, selectedTier),
    [selectedStat, selectedTier]
  );

  // Get forging info for current item and scroll
  const forgingInfo = useMemo(() =>
    selectedScroll ? getForgingInfo(currentItem, selectedScroll.id, gemstones) : null,
    [currentItem, selectedScroll, gemstones]
  );

  // Reset item when prop changes
  useEffect(() => {
    setCurrentItem(item);
    setForgeResult(null);
  }, [item?.instanceId]);

  // Hammer strike counter for animation
  const [hammerStrikes, setHammerStrikes] = useState(0);

  // Handle forge attempt
  const handleForge = useCallback(() => {
    if (!forgingInfo?.canApply || isForging) return;

    setIsForging(true);
    setForgeResult(null);
    setHammerStrikes(0);
    setAnimationPhase('forging');

    // Hammer strike sequence - 3 strikes with "ting" timing
    const strikeTimings = [0, 400, 800];
    strikeTimings.forEach((delay, index) => {
      setTimeout(() => {
        setHammerStrikes(index + 1);
      }, delay);
    });

    // After hammer strikes, do the flash
    setTimeout(() => {
      setAnimationPhase('flash');

      // Flash phase (0.4s)
      setTimeout(() => {
        // Perform forging
        const result = forgeItem(item.instanceId, selectedScroll.id);
        setForgeResult(result);
        setAnimationPhase('result');

        // Update local item state from result
        if (result && result.newItem) {
          setCurrentItem(result.newItem);
        }

        // Allow retry after seeing result
        setTimeout(() => {
          setIsForging(false);
          setAnimationPhase(null);
          setHammerStrikes(0);
        }, 600);
      }, 400);
    }, 1200);
  }, [forgingInfo, isForging, item.instanceId, selectedScroll, forgeItem]);

  // Skip animation
  const handleSkip = useCallback(() => {
    if (animationPhase === 'forging' || animationPhase === 'flash') {
      const result = forgeItem(item.instanceId, selectedScroll.id);
      setForgeResult(result);
      setAnimationPhase('result');
      setHammerStrikes(0);

      if (result && result.newItem) {
        setCurrentItem(result.newItem);
      }

      setTimeout(() => {
        setIsForging(false);
        setAnimationPhase(null);
      }, 500);
    }
  }, [animationPhase, item.instanceId, selectedScroll, forgeItem]);

  // Render gem slots visualization
  const renderGemSlots = () => {
    const maxSlots = currentItem.maxGemSlots || 0;
    const usedSlots = (currentItem.appliedScrolls || []).length;
    const failedSlots = currentItem.failedSlots || 0;
    const availableSlots = maxSlots - usedSlots - failedSlots;

    const slots = [];

    // Socketed slots (filled with gem tier indicator)
    for (let i = 0; i < usedSlots; i++) {
      const scroll = currentItem.appliedScrolls[i];
      const gemstone = GEMSTONES[SCROLL_TYPES[scroll.scrollId]?.gemstone];
      slots.push(
        <span
          key={`used-${i}`}
          className="fg-slot socketed"
          style={{ color: gemstone?.color || '#f5d742' }}
          title={`${gemstone?.name || 'Gem'} T${scroll.tier}: +${formatScrollStat(scroll.stat, scroll.value)} ${getStatLabel(scroll.stat)}`}
        >
          {scroll.tier}
        </span>
      );
    }

    // Failed slots (destroyed)
    for (let i = 0; i < failedSlots; i++) {
      slots.push(
        <span key={`failed-${i}`} className="fg-slot failed" title="Destroyed slot">
          X
        </span>
      );
    }

    // Available slots (empty)
    for (let i = 0; i < availableSlots; i++) {
      slots.push(
        <span key={`empty-${i}`} className="fg-slot empty" title="Available slot">

        </span>
      );
    }

    return slots;
  };

  // Get result message
  const getResultMessage = () => {
    if (!forgeResult) return null;

    if (forgeResult.success) {
      return (
        <div className="fg-result success">
          <span className="fg-result-icon">+</span>
          <span className="fg-result-text">
            Success! +{formatScrollStat(selectedScroll.stat, forgingInfo?.statBonus)} {getStatLabel(selectedScroll.stat)}
          </span>
        </div>
      );
    } else {
      return (
        <div className="fg-result fail">
          <span className="fg-result-icon">X</span>
          <span className="fg-result-text">
            Failed! Slot destroyed.
          </span>
        </div>
      );
    }
  };

  // Check if we can forge (has slots and resources)
  const canForge = forgingInfo?.canApply && !isForging;
  const remainingSlots = getRemainingSlots(currentItem);

  if (!item || !currentItem) return null;

  return (
    <div className="fg-overlay" onClick={onClose}>
      <div className="fg-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="fg-header">
          <span className="fg-header-icon">💎</span>
          <span className="fg-header-title">Socket Gems</span>
          <button className="fg-close" onClick={onClose}>×</button>
        </div>

        {/* Item Preview with Forge Animation */}
        <div className={`fg-item-preview ${animationPhase || ''}`}>
          {/* Anvil/Forge area */}
          <div className="fg-forge-area">
            {/* Sparks container */}
            {animationPhase === 'forging' && (
              <div className="fg-sparks-container">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className={`fg-spark spark-${i} ${hammerStrikes > 0 ? 'active' : ''}`}
                    style={{ '--spark-delay': `${i * 0.05}s`, '--spark-angle': `${(i * 45) - 180}deg` }}
                  />
                ))}
              </div>
            )}

            {/* Hammer */}
            {animationPhase === 'forging' && (
              <div className={`fg-hammer strike-${hammerStrikes}`}>🔨</div>
            )}

            {/* Item on anvil */}
            <div className="fg-anvil">
              <div className={`fg-item-container ${hammerStrikes > 0 ? `struck-${hammerStrikes}` : ''}`}>
                <ItemChip item={currentItem} size="large" showName={false} />
              </div>
              {/* Ting text */}
              {animationPhase === 'forging' && hammerStrikes > 0 && (
                <div className={`fg-ting ting-${hammerStrikes}`}>✨ TING!</div>
              )}
            </div>

            {/* Glow effect during forging */}
            {animationPhase === 'forging' && (
              <div className={`fg-glow glow-${hammerStrikes}`} />
            )}

            {/* Final flash effect */}
            {animationPhase === 'flash' && (
              <div className="fg-final-flash" />
            )}
          </div>

          <div className="fg-slot-display">
            {renderGemSlots()}
          </div>
          <div className="fg-slot-count">
            {remainingSlots}/{currentItem.maxGemSlots || 0} slots
          </div>
          <div className="fg-item-name">{item.name}</div>
        </div>

        {/* No slots remaining */}
        {remainingSlots === 0 ? (
          <div className="fg-no-slots">
            <span className="fg-no-slots-icon">X</span>
            <span className="fg-no-slots-text">No gem slots remaining!</span>
          </div>
        ) : (
          <>
            {/* Gem Type Selection */}
            <div className="fg-section">
              <div className="fg-section-label">Gem Type</div>
              <div className="fg-stat-buttons">
                {SCROLL_STAT_TYPES.map(statType => {
                  const gemstone = GEMSTONES[statType.gemstone];
                  const isSelected = selectedStat === statType.stat;
                  return (
                    <button
                      key={statType.stat}
                      className={`fg-stat-btn ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedStat(statType.stat)}
                      style={isSelected ? { borderColor: gemstone.color, boxShadow: `0 0 8px ${gemstone.color}40` } : {}}
                      disabled={isForging}
                    >
                      <span className="fg-stat-icon">{statType.icon}</span>
                      <span className="fg-stat-label">{statType.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Gem Tier Selection */}
            <div className="fg-section">
              <div className="fg-section-label">Gem Tier</div>
              <div className="fg-tier-buttons">
                {SCROLL_TIERS.map(tier => {
                  const isSelected = selectedTier === tier;
                  const rate = Math.round(FORGING_CONFIG.successRates[tier] * 100);
                  return (
                    <button
                      key={tier}
                      className={`fg-tier-btn ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedTier(tier)}
                      disabled={isForging}
                    >
                      <span className="fg-tier-num">{tier}</span>
                      <span className="fg-tier-rate">{rate}%</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Gem Preview */}
            {selectedScroll && (
              <div className="fg-gem-preview">
                <div className="fg-gem-name">{GEMSTONES[selectedScroll.gemstone]?.name} T{selectedTier}</div>
                <div className="fg-gem-info">
                  <span className="fg-gem-rate">
                    Success: {Math.round(FORGING_CONFIG.successRates[selectedTier] * 100)}%
                  </span>
                  <span className="fg-gem-bonus">
                    Bonus: {formatScrollStat(selectedScroll.stat, FORGING_CONFIG.statBonuses[selectedTier][selectedScroll.stat])} {getStatLabel(selectedScroll.stat)}
                  </span>
                </div>
              </div>
            )}

            {/* Cost Display */}
            {selectedScroll && (
              <div className={`fg-cost ${forgingInfo?.canAfford ? 'can-afford' : 'cannot-afford'}`}>
                <span className="fg-cost-label">Cost:</span>
                <span className="fg-cost-value">
                  <span
                    className="fg-gem-icon"
                    style={{ color: GEMSTONES[selectedScroll.gemstone]?.color }}
                  >
                    {GEMSTONES[selectedScroll.gemstone]?.icon}
                  </span>
                  {forgingInfo?.gemCost || 0}
                </span>
                <span className="fg-cost-have">
                  (You have: <span
                    className="fg-gem-icon"
                    style={{ color: GEMSTONES[selectedScroll.gemstone]?.color }}
                  >
                    {GEMSTONES[selectedScroll.gemstone]?.icon}
                  </span>
                  {gemstones[selectedScroll.gemstone] || 0})
                </span>
              </div>
            )}

            {/* Result Message */}
            {getResultMessage()}

            {/* Forge Button */}
            <div className="fg-actions">
              {isForging && animationPhase !== 'result' ? (
                <button className="fg-skip-btn" onClick={handleSkip}>
                  Skip
                </button>
              ) : (
                <button
                  className={`fg-forge-btn ${canForge ? '' : 'disabled'}`}
                  onClick={handleForge}
                  disabled={!canForge}
                >
                  <span className="fg-forge-icon">💎</span>
                  Socket
                  <span className="fg-forge-icon">💎</span>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ForgingModal;
